/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { HotelBooking } from '@app/hotels/hotel-bookings/hotel-booking.model';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HotelBookingsService } from '@app/hotels/hotel-bookings/hotel-bookings.service';
import { AuthenticationService, Logger } from '@app/core';
import * as moment from 'moment';
import { ApiQuery } from '@app/core/http/api-query';
import { Room } from '@app/hotels/rooms/room.model';
import { Night } from '@app/hotels/rooms/night.model';
import { CurrencySelectComponent } from '@app/shared/components/currency-select/currency-select.component';
import { Currency } from '@app/settings/currencies/currency.model';
import { Router } from '@angular/router';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { environment } from '@env/environment';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { groupBy, unionBy, cloneDeep, meanBy, findIndex } from 'lodash';
import swal, { SweetAlertOptions } from 'sweetalert2';
import { SaleFinal } from '@app/sales/sales/sale-final.model';
import { HotelBookingsFormService } from '@app/hotels/hotel-bookings/hotel-bookings-form.service';

const log = new Logger('HotelBookingsFormComponent');

@Component({
  selector: 'app-hotel-bookings-form',
  templateUrl: './hotel-bookings-form.component.html',
  styleUrls: ['./hotel-bookings-form.component.scss'],
})
export class HotelBookingsFormComponent implements OnInit {
  @Input() hotelBooking: HotelBooking = null;
  form: FormGroup;
  @ViewChild('currencySelectComponent', { static: true }) currencySelectComponent: CurrencySelectComponent;
  LaravelModelsEnum = LaravelModelsEnum;
  env = environment;
  UserRoleType = UserRoleType;
  @Input() showOnlyAllotmentId: string = null;
  @Input() showOnlyNights: Array<Night> = null;
  cancellationDeadlineMinDate: string | Date = moment().toDate();
  cancellationDeadlineMaxDate: string | Date = null;
  showAllRooms = true;
  grouped = true;
  groupedRooms: any;
  meanBy = meanBy;
  @Input() final: SaleFinal = null;

  currentBookingIndex: number = null;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private hotelBookingService: HotelBookingsService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService,
    private hotelBookingsFormService: HotelBookingsFormService
  ) {}

  ngOnInit() {
    this.createForm();
    this.patchForm();

    this.form.get('currency_id').valueChanges.subscribe((val: number) => {
      if (val) {
        //  log.debug('call');
        this.updateAllRoomsAndNightsPriceValidation();
      }
    });

    if (this.showOnlyAllotmentId || this.showOnlyNights) {
      this.showAllRooms = false;
    }

    if (this.final && this.final.items) {
      this.currentBookingIndex = findIndex(this.final.items, (item: any) => {
        return item.hotel_booking_nights === this.showOnlyNights;
      });
    }
  }

  save(close: boolean = true) {
    if (this.hotelBooking && this.hotelBooking.id) {
      return new Promise((resolve, reject) => {
        // Subscriber fn...
        const hotelBooking: HotelBooking = this.hotelBooking.deserialize(this.formatPost());
        this.hotelBookingService.update(hotelBooking).subscribe((res) => {
          if (close) {
            this.modal.close(res);
          }
          resolve(() => {});
        });
      });
    } else {
      const hotelBooking: HotelBooking = new HotelBooking().deserialize(this.formatPost());
      this.hotelBookingService.store(hotelBooking).subscribe((res) => this.modal.close(res));
    }
  }

  formatPost() {
    let formattedPost = {};

    if (this.grouped && this.hotelBooking && this.hotelBooking.id) {
      const formCopy = cloneDeep(this.form.value);
      const newRooms: Array<any> = [];

      formCopy.rooms.forEach((room: any) => {
        for (let i = 0; i < Number(room.count); i++) {
          const newRoom = cloneDeep(room);
          newRoom.id =
            this.groupedRooms[room.allotment_id] && this.groupedRooms[room.allotment_id][i]
              ? this.groupedRooms[room.allotment_id][i].id
              : null;
          newRoom.count = 1;
          newRoom.ref_number =
            this.groupedRooms[room.allotment_id] && this.groupedRooms[room.allotment_id][i]
              ? this.groupedRooms[room.allotment_id][i].ref_number
              : null;
          newRooms.push(newRoom);
        }
      });

      formCopy.rooms = newRooms;
      formattedPost = formCopy;
    } else {
      formattedPost = this.form.value;
    }

    return formattedPost;
  }

  addRoom() {
    const rooms = this.form.get('rooms') as FormArray;
    rooms.push(this.getEmptyRoom(0, true));

    /**
     * Ако има избран Event в app-events-select, който има дати
     * this.getEmptyRoom ще ги попълни.
     * След като ги попълни се вика функцията this.datesChange, за да попълни и nights
     */
    if (
      this.form &&
      this.form.value.events.length > 0 &&
      this.form.value.events[0].date_from &&
      this.form.value.events[0].date_to
    ) {
      setTimeout(() => {
        this.datesChange(this.form.get('rooms')['controls'].length - 1);
      });
    }
  }

  removeRoom(index: number) {
    this.form.get('rooms')['controls'].splice(index, 1);
    this.form.get('rooms').updateValueAndValidity();
    this.findCancellationDeadlineMaxDate();
  }

  /**
   * Пълни полетата в таблицата с nights. Трябва само индекса, за да се намери конкретната стая понеже са масив.
   * По втория параметър познава и полето от което ще вземе стойностите, за да ги напълни
   * @param roomIndex Index-а на стаята
   * @param toControlName Името на formControl-ите в който се пише стойността
   */
  fillNightsControls(roomIndex: number, toControlName: string): void {
    // стойността, която трябва да се попълни във formControl-a
    let value = this.form.get('rooms')['controls'][roomIndex]['controls']['table_foot_' + toControlName].value;

    // ако дойде празен стринг или нещо гръмне горе ще напълни null
    // не трябва да пълним formControl-и с празни стрингове - по добре с null(за селекти най-вече)
    if (!value) {
      value = null;
    }

    this.form
      .get('rooms')
      ['controls'][roomIndex]['controls']['nights']['controls'].forEach((control: any, nightIndex: number) => {
        /**
         * Ако нощувката е not available я skip-ваме
         */
        // if (control.get('isAvailable').value) {
        control.get(toControlName).setValue(value);
        if (toControlName === 'margin') {
          this.nightMarginChange(roomIndex, nightIndex);
        }
        if (toControlName === 'price' || toControlName === 'net_price') {
          this.nightPriceChange(roomIndex, nightIndex);
        }
        //  }
      });
  }

  /**
   * Попълва formControl-а "nights_count" при промяна на някоя от датите (check_in или check_out)
   * Това е закачено към всяка стая.
   * Нужен е само индекса на стаята. Функцията сама си намира датите благодарение на подадения индекс на стаята
   * @param i Индекса на стаята
   */
  datesChange(i: number): void {
    this.findCancellationDeadlineMaxDate();
    const checkInDate = this.form.get('rooms')['controls'][i].get('check_in').value;
    const checkOutDate = this.form.get('rooms')['controls'][i].get('check_out').value;
    // log.debug('ii', i);
    //  log.debug('checkInDate', checkInDate);
    // log.debug('checkOutDate', checkOutDate);
    if (!checkInDate || !checkOutDate) {
      return log.warn('invalid checkInDate or checkOutDate');
    }
    const daysDiff = moment(checkOutDate).diff(moment(checkInDate), 'days');

    this.form.get('rooms')['controls'][i].get('nights_count').setValue(daysDiff);
  }

  /**
   * Попълва formControl-а "fake_margin" при промяна на някоя от цените (fake_net_price или fake_price)
   * Това е закачено към всяка стая.
   * Нужен е само индекса на стаята. Функцията сама си намира цените благодарение на подадения индекс на стаята
   * @param i Индекса на стаята
   */
  roomPriceChange(i: number): void {
    const fakeNetPrice = this.toEUR(Number(this.form.get('rooms')['controls'][i].get('fake_net_price').value));
    const fakePrice = Number(this.form.get('rooms')['controls'][i].get('fake_price').value);
    let fakeMargin = Number((fakePrice - fakeNetPrice).toFixed(2));

    if (!fakeNetPrice || !fakePrice) {
      fakeMargin = null;
      log.warn('invalid fake_net_price or fake_price');
    }

    this.form.get('rooms')['controls'][i].get('fake_margin').setValue(fakeMargin);
  }

  /**
   * Попълва formControl-а "fake_price" при промяна на margin-a
   * Това е закачено към всяка стая.
   * Нужен е само индекса на стаята. Функцията сама си намира net_price и margin благодарение на подадения индекс на стаята
   * @param i Индекса на стаята
   */
  roomMarginChange(i: number): void {
    const fakeNetPrice = this.toEUR(Number(this.form.get('rooms')['controls'][i].get('fake_net_price').value));
    const fakeMargin = Number(this.form.get('rooms')['controls'][i].get('fake_margin').value);
    let fakePrice = Number((fakeNetPrice + fakeMargin).toFixed(2));

    if (!fakePrice) {
      fakePrice = null;
      log.warn('invalid fake_net_price or fake_margin');
    }

    this.form.get('rooms')['controls'][i].get('fake_price').setValue(fakePrice);
  }

  roomStatusChange(roomIndex: number): void {
    const rooms = this.form.get('rooms') as FormArray;
    const room = rooms.at(roomIndex) as FormGroup;
    const nights = room.get('nights') as FormArray;
    const status = room.get('status')!.value;

    nights.controls.forEach((night) => {
      (night as FormGroup).get('status')!.setValue(status);
    });
  }

  /**
   * Попълва formControl-а "margin" при промяна на някоя от цените (net_price или price)
   * Това е закачено към всяка нощувка.
   * Нужен е само индекса на стаята. Функцията сама си намира цените благодарение на подадения индекс на нощувка
   * @param roomIndex Индекса на стаята
   * @param nightIndex Индекса на нощувката
   */
  nightPriceChange(roomIndex: number, nightIndex: number): void {
    const netPrice = this.toEUR(
      Number(this.form.get('rooms')['controls'][roomIndex].get('nights')['controls'][nightIndex].get('net_price').value)
    );
    const price = Number(
      this.form.get('rooms')['controls'][roomIndex].get('nights')['controls'][nightIndex].get('price').value
    );
    let margin = Number((price - netPrice).toFixed(2));

    if (!netPrice || !price) {
      margin = null;
      log.warn('invalid net_price or price');
    }

    this.form.get('rooms')['controls'][roomIndex].get('nights')['controls'][nightIndex].get('margin').setValue(margin);
  }

  /**
   * Попълва formControl-а "price" при промяна на margin-a
   * Това е закачено към всяка нощувка.
   * Нужен е само индекса на стаята. Функцията сама си намира margin-a и цената благодарение на подадения индекс на нощувка
   * @param roomIndex Индекса на стаята
   * @param nightIndex Индекса на нощувката
   */
  nightMarginChange(roomIndex: number, nightIndex: number): void {
    const netPrice = this.toEUR(
      Number(this.form.get('rooms')['controls'][roomIndex].get('nights')['controls'][nightIndex].get('net_price').value)
    );
    const margin = Number(
      this.form.get('rooms')['controls'][roomIndex].get('nights')['controls'][nightIndex].get('margin').value
    );
    let price = Number((netPrice + margin).toFixed(2));

    if (!price) {
      price = null;
      log.warn('invalid net_price or margin');
    }

    this.form.get('rooms')['controls'][roomIndex].get('nights')['controls'][nightIndex].get('price').setValue(price);
  }

  /**
   * Попълва formControl-а "check_out" при промяна на formControl-а "nights_count"
   * Функцията изчислява check_out дата-та като използва за входящи данни check_in и nights_count
   * @param i Индекса на стаята
   */
  nightsCountChange(i: number): void {
    const checkInDate = this.form.get('rooms')['controls'][i].get('check_in').value;
    const nightsCount = this.form.get('rooms')['controls'][i].get('nights_count').value;
    if (!checkInDate || !nightsCount) {
      return log.warn('invalid checkInDate or nightsCount');
    }
    const newCheckOutDate = moment(checkInDate).add(nightsCount, 'days').format(this.env.apiPipeDateFormat);
    this.form.get('rooms')['controls'][i].get('check_out').setValue(newCheckOutDate);
  }

  goToBooking(newIndex: number) {
    log.debug('newIndex', newIndex);
    if (!this.final.items[newIndex]) {
      return log.error('Can`t find items with this index');
    }

    if (this.form.invalid) {
      return swal
        .fire({
          type: 'error',
          title: 'Invalid field/s found',
        } as SweetAlertOptions)
        .then((result) => {
          return false;
        });
    }

    const hotelBooking = new HotelBooking().deserialize({
      id: this.final.items[newIndex].hotel_booking_room?.booking.id,
    });
    const showOnlyNights = this.final.items[newIndex].hotel_booking_nights;

    if (this.form.dirty) {
      swal
        .fire({
          type: 'warning',
          title: 'Do you want to save before switch bookings?',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        } as SweetAlertOptions)
        .then((result) => {
          if (result.value) {
            // on confirm
            this.save().then(() => {
              this.hotelBookingsFormService.open(hotelBooking, null, showOnlyNights, true, this.final);
            });
          } else {
            this.modal.dismiss();
            this.hotelBookingsFormService.open(hotelBooking, null, showOnlyNights, true, this.final);
          }
        });
    } else {
      this.modal.dismiss();
      this.hotelBookingsFormService.open(hotelBooking, null, showOnlyNights, true, this.final);
    }
  }

  test(form: any) {
    log.debug(form);
  }

  /**
   * Сетва дата на инпут
   */
  fillDeadlineDate(formControlName: string, value: number | string, periodType: string, popover: any): void {
    value = Number(value);

    const allDates: Array<any> = [];

    this.form.get('rooms')['controls'].forEach((roomControl: any) => {
      roomControl.get('nights')['controls'].forEach((control: any) => {
        if (control.get('date').value) {
          //  log.debug(control.get('date').value);
          allDates.push(moment(control.get('date').value));
        }
      });
    });

    const minOfAllDates = moment.min(allDates);
    // @ts-ignore
    let dateToSet = minOfAllDates.subtract(value, periodType);

    // допълнителна логика за cancellation_deadline от #344: hotel booking cancellation deadline
    if (formControlName === 'cancellation_deadline') {
      const cancellationDeadlineMinDate = moment(this.cancellationDeadlineMinDate);
      const cancellationDeadlineMaxDate = moment(this.cancellationDeadlineMaxDate);

      if (dateToSet.isBefore(cancellationDeadlineMinDate)) {
        dateToSet = cancellationDeadlineMinDate;
      }

      if (minOfAllDates.isAfter(cancellationDeadlineMaxDate)) {
        dateToSet = cancellationDeadlineMaxDate;
      }

      this.form.get(formControlName).setValue(dateToSet);
    } else {
      this.form.get(formControlName).setValue(dateToSet);
    }

    popover.close();
  }

  goToCancellation(hotelBookingCancellationId: number) {
    this.router.navigate(['/hotel-booking-cancellations/view'], {
      queryParams: { id: hotelBookingCancellationId },
      replaceUrl: true,
    });
    this.modal.dismiss('goToCancellation');
  }

  goToSale(saleId: number) {
    this.router.navigate(['/sales/view'], {
      queryParams: { id: saleId },
      replaceUrl: true,
    });
    this.modal.dismiss('goToCancellation');
  }

  findCancellationDeadlineMaxDate() {
    if (this.form.get('rooms')['controls'].length > 0) {
      let minDate = this.form.get('rooms')['controls'][0].value.check_in;
      this.form.get('rooms')['controls'].forEach((roomControl: any) => {
        if (moment(roomControl.get('check_in').value).isBefore(moment(minDate))) {
          minDate = roomControl.get('check_in').value;
        }
      });
      this.cancellationDeadlineMaxDate = moment(minDate).subtract(1, 'days').format(this.env.apiPipeDateFormat);
    } else {
      this.cancellationDeadlineMaxDate = null;
    }
  }

  duplicate() {
    this.grouped = false;
    this.hotelBooking.id = null;

    this.form.get('rooms')['controls'].forEach((roomControl: any) => {
      roomControl.get('ref_number').patchValue(null);
      roomControl.get('count').enable();
    });
    this.form.get('ref_number').patchValue(null);

    this.translate.get('HOTEL_BOOKINGS.HOTEL_BOOKINGS_FORM.DUPLICATED_MSG').subscribe((trans: string) => {
      this.toastr.success(trans);
    });
  }

  /**
   *
   */
  toggleHiddenRooms() {
    this.showAllRooms = !this.showAllRooms;
  }

  /**
   * Групира и разгрупира алотмантите
   */
  toggleGroupedRooms() {
    if (this.form.invalid) {
      return swal
        .fire({
          type: 'error',
          title: 'Invalid field/s found',
        } as SweetAlertOptions)
        .then((result) => {
          return false;
        });
    }

    if (this.form.dirty) {
      swal
        .fire({
          type: 'warning',
          title: 'You have to save before switch!',
          showCancelButton: true,
        } as SweetAlertOptions)
        .then((result) => {
          if (result.value) {
            // on confirm
            this.save(false).then(() => {
              this.grouped = !this.grouped;
              this.patchForm();
            });
          }
        });
    } else {
      this.grouped = !this.grouped;
      this.patchForm();
    }
  }

  /**
   * слага и маха disabled от room count полето
   */
  private handleCountDisable() {
    this.form.get('rooms')['controls'].forEach((roomControl: any) => {
      if (roomControl.get('id').value && this.grouped) {
        roomControl.get('count').enable();
      } else {
        roomControl.get('count').disable();
      }
      // roomControl.get('count').updateValueAndValidity({onlySelf: true, emitEvent: true});
    });
  }

  /**
   * Ъпдейтва валидацията на всички стаи и нощувки
   *
   * За сега се използва, когато се сменя валутата, тригерне change на net_price
   * и от там да се пормени валидацията на другите цени
   */
  private updateAllRoomsAndNightsPriceValidation(): void {
    setTimeout(() => {
      this.form.get('rooms')['controls'].forEach((roomControl: any) => {
        // log.debug('roomControl', roomControl.get('fake_net_price'));
        if (roomControl.get('fake_net_price').value) {
          roomControl.get('fake_net_price').updateValueAndValidity({ onlySelf: true, emitEvent: true });
        }
        roomControl.get('nights')['controls'].forEach((control: any) => {
          if (control.get('net_price').value) {
            //  log.debug('update net price validation', control);
            control.get('net_price').updateValueAndValidity({ onlySelf: true, emitEvent: true });
          }
        });
      });
    });
  }

  /**
   * Попълва формата при едит
   */
  private patchForm(): void {
    if (this.hotelBooking && this.hotelBooking.id) {
      const query = new ApiQuery();
      query.addIncludes(
        'events',
        'rooms.nights',
        'hotel',
        'supplier',
        'company',
        'currency',
        'contracting_agent',
        'reservation_agent',
        'amendment_agent',
        'tags'
      );
      this.hotelBookingService.show(this.hotelBooking.id, query).subscribe((hotelBooking: HotelBooking) => {
        if (hotelBooking['rooms'].length > 0) {
          let groupedRooms: any = null;
          if (this.grouped) {
            this.groupedRooms = groupedRooms = groupBy(hotelBooking.rooms, 'allotment_id');
            hotelBooking.rooms = unionBy(hotelBooking.rooms, 'allotment_id');
          }

          const rooms = new FormArray([]);
          hotelBooking.rooms.forEach((room: Room) => {
            /*
             * Проверява разликата между датите в дни, за да знае колко night FormGroup-a да попълни,
             * които по-късно ще пачне
             */
            const nightsCount = moment(room.check_out).diff(moment(room.check_in), 'days');
            rooms.push(this.getEmptyRoom(nightsCount));

            if (this.grouped) {
              room.count = groupedRooms[room.allotment_id].length;
            } else {
              // при edit разбиваме стаите, за да може да ги променят за това сетвам 1 на count
              room.count = 1;
            }

            room.nights_count = nightsCount;

            // проверява дали има разлики в цените на нощувките start
            if (room.nights.length > 0) {
              //  log.debug('Number(this.toEUR(room.nights[0].net_price))', hotelBooking.currency);
              const netPrice = room.nights[0].net_price;
              const price = room.nights[0].price;
              const margin = Number(
                (
                  Number(room.nights[0].price) - Number(this.toEUR(room.nights[0].net_price, hotelBooking.currency))
                ).toFixed(2)
              );
              const webPrice = room.nights[0].web_price;

              /**
               * Ако при отваряне на формата са подадени showOnlyNights или showOnlyAllotmentId
               * слага допълнително мета поле 'hidden', което по-късно се пачва във формата
               * и съответно се използва в темплейта, за да показва само подадените стаи/нощувки
               */
              if ((this.showOnlyNights && this.showOnlyNights.length > 0) || this.showOnlyAllotmentId) {
                room['hidden'] = true;
              }

              if (room.allotment_id === this.showOnlyAllotmentId) {
                room['hidden'] = false;
              }

              room.nights.forEach((night: Night) => {
                //  night.date = moment(night.date).format('DD.MM.YYYY');
                if (netPrice !== night.net_price || price !== night.price || webPrice !== night.web_price) {
                  // Ако намери разлика цъка отметката different_price
                  room['different_price'] = true;
                }

                // Пълни margin на всяка нощувка
                night['margin'] = Number(
                  (Number(night.price) - Number(this.toEUR(night.net_price, hotelBooking.currency))).toFixed(2)
                );

                night['isAvailable'] = new Night().deserialize(night).isNightAvailable();

                // скрива стаята ако някоя нощувка не е available само когато е подадено showOnlyAllotmentId
                if (!night['isAvailable'] && this.showOnlyAllotmentId) {
                  room['hidden'] = true;
                }

                /**
                 * Ако намери една нощувка в масива showOnlyNights прави видима цялата стая
                 */
                if (this.showOnlyNights && this.showOnlyNights.length > 0) {
                  this.showOnlyNights.forEach((n: Night) => {
                    if (n.id === night.id) {
                      room['hidden'] = false;
                    }
                  });
                }
              });

              // Не намери ли разлики в цените пълни fake control-ите
              if (!room['different_price']) {
                room['fake_net_price'] = netPrice;
                room['fake_price'] = price;
                room['fake_margin'] = margin;
                room['fake_web_price'] = webPrice;
              }
            }
            // проверява дали има разлики в цените на нощувките end

            room['availableNights'] = new Room().deserialize(room).getAvailableNights();
          });
          //  log.debug('rooms', rooms);
          this.form.setControl('rooms', rooms);
        }

        this.cancellationDeadlineMinDate = hotelBooking.created_at.date;
        // след всичко по-горе patch-ва формата
        this.form.patchValue(hotelBooking);

        this.form.updateValueAndValidity();

        this.findCancellationDeadlineMaxDate();
        this.handleCountDisable();
      });
    } else if (this.hotelBooking) {
      this.patchContractingAgent();
      this.patchReservationAgent();
      this.patchAmendmentAgent();

      setTimeout(() => {
        if (this.hotelBooking['rooms'].length > 0) {
          const rooms = new FormArray([]);
          this.hotelBooking.rooms.forEach((room: any) => {
            /*
             * Проверява разликата между датите в дни, за да знае колко night FormGroup-a да попълни,
             * които по-късно ще пачне
             */
            const nightsCount = moment(room.check_out).diff(moment(room.check_in), 'days');
            room.nights_count = nightsCount;

            const margin = Number(
              (Number(room.fake_price) - Number(this.toEUR(room.fake_net_price, this.hotelBooking.currency))).toFixed(2)
            );
            room['fake_margin'] = margin;

            rooms.push(this.getEmptyRoom(nightsCount));
          });

          this.form.setControl('rooms', rooms);
          this.findCancellationDeadlineMaxDate();
        }

        this.form.patchValue(this.hotelBooking);
        this.drawNights(0);
      });
    } else {
      this.patchContractingAgent();
      this.patchReservationAgent();
      this.patchAmendmentAgent();
    }
  }

  /**
   * Попълва contracting_agent с логнатия user
   */
  private patchContractingAgent() {
    this.authenticationService.loadProfile().subscribe((profile: any) => {
      this.form.get('contracting_agent_id').patchValue(profile.profile.id);
      this.form.get('contracting_agent').patchValue(profile.profile);
    });
  }

  /**
   * Попълва reservation_agent с логнатия user
   */
  private patchReservationAgent() {
    this.authenticationService.loadProfile().subscribe((profile: any) => {
      this.form.get('reservation_agent_id').patchValue(profile.profile.id);
      this.form.get('reservation_agent').patchValue(profile.profile);
    });
  }

  /**
   * Попълва amendment_agent с логнатия user
   */
  private patchAmendmentAgent() {
    const amendmentAgentIdCtrl = this.form.get('amendment_agent_id');
    const amendmentAgentCtrl = this.form.get('amendment_agent');
     // Do not overwrite an already selected / prefilled amendment agent
    if (amendmentAgentIdCtrl?.value) {
       return;
    }
    this.authenticationService.loadProfile().subscribe((profile: any) => {
      // Guard again in case the value was set before the async callback executes
      if (!amendmentAgentIdCtrl?.value) {
        amendmentAgentIdCtrl?.patchValue(profile.profile.id);
        amendmentAgentCtrl?.patchValue(profile.profile);
      }
    });
  }

  private createForm(): void {
    const rooms = new FormArray([]);
    //  rooms.push(this.getEmptyRoom());

    const supplierId = new FormControl(null);

    const cancellationTermsId = new FormControl(null, [Validators.required]);
    const commissionPercent = new FormControl(null);
    const depositPercent = new FormControl(null, [Validators.required]);
    const cancellationChargePercent = new FormControl(null, [Validators.required]);

    const breakfastPricePerNight = new FormControl(null);
    const breakfastPricePerNightCurrencyId = new FormControl(null);
    const showToWeb = new FormControl(false);
    const breakfast = new FormControl(false);

    supplierId.valueChanges.subscribe((val) => {
      if (val) {
        cancellationTermsId.clearValidators();
        depositPercent.clearValidators();
        cancellationChargePercent.clearValidators();
      } else {
        cancellationTermsId.setValidators([Validators.required]);
        depositPercent.setValidators([Validators.required]);
        cancellationChargePercent.setValidators([Validators.required]);
      }

      cancellationTermsId.updateValueAndValidity();
      depositPercent.updateValueAndValidity();
      cancellationChargePercent.updateValueAndValidity();
    });

    this.form = this.formBuilder.group({
      hotel_id: [null, [Validators.required]],
      hotel: [null],
      supplier_id: supplierId,
      supplier: [null],
      company_id: [null, [Validators.required]],
      company: [null],
      commission_percent: commissionPercent,
      deposit_percent: depositPercent,
      cancellation_charge_percent: cancellationChargePercent,
      ref_number: [null],
      property_id: [null, [Validators.required]],
      cancellation_deadline: [null, [Validators.required]],
      payment_deadline: [null, [Validators.required]],
      currency_id: [null, [Validators.required]],
      currency: [null],
      events: [[], [Validators.required]],
      rooms: rooms,
      breakfast: breakfast,
      note: [null],
      contracting_agent_id: [null, [Validators.required]],
      contracting_agent: [null],
      reservation_agent_id: [null, [Validators.required]],
      reservation_agent: [null],
      amendment_agent_id: [null],
      cancellation_terms_id: cancellationTermsId,
      cancellation_terms: [null],
      show_to_web: showToWeb,
      breakfast_price_per_night: breakfastPricePerNight,
      breakfast_price_per_night_currency_id: breakfastPricePerNightCurrencyId,
      tags: [],
      created_at: [null],
      offer_deadline: [null],
      book: [false],
    });
  }

  private getEmptyRoom(nightsCountToCreate: number = 0, fillDates: boolean = false): FormGroup {
    let formGroup: FormGroup;
    const nightsCount = new FormControl(null, [Validators.required]);
    const differentPrice = new FormControl(false);
    const fakeNetPrice = new FormControl(null, [Validators.required]);
    const fakeNetPricePriceEuroValue = this.toEUR(fakeNetPrice.value);
    const fakePrice = new FormControl(null, [Validators.required, Validators.min(fakeNetPricePriceEuroValue)]);
    const fakeMargin = new FormControl(null, [Validators.required, Validators.min(0)]);
    const fakeWebPrice = new FormControl(null, [Validators.required, Validators.min(fakeNetPricePriceEuroValue)]);
    const nights = new FormArray([]);
    const status = new FormControl('', []);

    /*
     * При промяна да броя на нощувките се грижи за nights FormArray-a.
     * На функцията и е нужен само индекса на стаята
     */

    setTimeout(() => {
      nightsCount.valueChanges.subscribe((val) => {
        this.drawNights(this.form.get('rooms')['controls'].indexOf(formGroup));

        /*
         * При промяна да броя на нощувките проверява дали е чекнат чекбокса differentPrice
         * Ако е false значи FormControl-ите в таблицата са скрити и не се виждат, но се валидират
         * Не трябва да се допуска някой night да е с празни FormControl-и за това тук се пълнят
         * За повече подробности как точно се случва погледни функцията fakeControlsChangeHandle()
         */

        // имаше проблем за това съкратих малко проверката, оставям стария вариант за всеки случай
        //   if (!differentPrice.value && fakeNetPrice.value && fakePrice.value && fakeMargin.value && fakeWebPrice.value) {
        if (!differentPrice.value) {
          this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'net_price');
          this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'price');
          this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'margin');
          this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'web_price');
        }
      });
    });
    /*
     * Долните няколко са идентични.
     * Грижат се при попълване на цената във fake FormControl-ите новите стойности да се отразят и във вече създадените
     * nights. Ако по-късно се добавят още nights те няма да получат стойностите от fake полетата.
     * На функциите им е нужен само индекса на стаята
     */

    fakeNetPrice.valueChanges.subscribe((val) => {
      this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'net_price');

      if (!differentPrice.value) {
        const fakeNetPriceEuroValue = this.toEUR(val);
        fakePrice.setValidators([Validators.required, Validators.min(fakeNetPriceEuroValue)]);
        fakeWebPrice.setValidators([Validators.required, Validators.min(fakeNetPriceEuroValue)]);
        fakeMargin.setValidators([Validators.required, Validators.min(0)]);
        fakePrice.updateValueAndValidity();
        fakeWebPrice.updateValueAndValidity();
        fakeMargin.updateValueAndValidity();
      }
    });

    fakePrice.valueChanges.subscribe((val) => {
      this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'price');
    });

    fakeMargin.valueChanges.subscribe((val) => {
      this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'margin');
    });

    fakeWebPrice.valueChanges.subscribe((val) => {
      this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'web_price');
    });

    for (let i = 0; i < nightsCountToCreate; i++) {
      nights.push(this.getEmptyNight());
    }

    /*
     * Тук се прави валидацията на fake FormControl-ите.
     * Ако е избрана отметката different_price fake FormControl-ите вече не са задължителни.
     * Ако не е избрана прави fake FormControl-ите задължителни.
     */

    differentPrice.valueChanges.subscribe((val: boolean) => {
      if (val) {
        fakeNetPrice.clearValidators();
        fakePrice.clearValidators();
        fakeMargin.clearValidators();
        fakeWebPrice.clearValidators();
      } else {
        const fakeNetPriceEuroValue2 = this.toEUR(fakeNetPrice.value);
        fakeNetPrice.setValidators([Validators.required]);
        fakePrice.setValidators([Validators.required, Validators.min(fakeNetPriceEuroValue2)]);
        fakeMargin.setValidators([Validators.required, Validators.min(0)]);
        fakeWebPrice.setValidators([Validators.required, Validators.min(fakeNetPriceEuroValue2)]);

        /*
         * Когато ънчекне differentPrice попълва всички FormControl-и в таблицата от fake FormControl-ите
         * За повече подробности как точно се случва погледни функцията fakeControlsChangeHandle()
         */
        this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'net_price');
        this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'price');
        this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'margin');
        this.fakeControlsChangeHandle(this.form.get('rooms')['controls'].indexOf(formGroup), 'web_price');
      }
      /*
       *  {emitEvent: false} ъпдейтва, но не пали valueChanges. Ако има нещо закачено не се вика
       *  В случая целта ни е да ъпдейтнем валидатора без да палим change евентите.
       *  Има такива (примерно: fakeControlsChangeHandle) и става мармалад
       */
      fakeNetPrice.updateValueAndValidity({ emitEvent: false });
      fakePrice.updateValueAndValidity({ emitEvent: false });
      fakeMargin.updateValueAndValidity({ emitEvent: false });
      fakeWebPrice.updateValueAndValidity({ emitEvent: false });
    });

    // пълни дата от първия Event ако има избран такъв
    let checkInDate = null;
    let checkOutDate = null;
    if (fillDates && this.form && this.form.value.events.length > 0 && this.form.value.events[0].date_from) {
      checkInDate = this.form.value.events[0].date_from;
    }
    if (fillDates && this.form && this.form.value.events.length > 0 && this.form.value.events[0].date_to) {
      checkOutDate = this.form.value.events[0].date_to;
    }

    formGroup = new FormGroup({
      id: new FormControl(null),
      check_in: new FormControl(checkInDate, [Validators.required]),
      nights_count: nightsCount,
      check_out: new FormControl(checkOutDate, [Validators.required]),
      min_nights_from: new FormControl(null),
      min_nights_to: new FormControl(null),
      type_id: new FormControl(null, [Validators.required]),
      count: new FormControl(null, [Validators.required]), // rooms count
      meal_plan_id: new FormControl(null, [Validators.required]),
      people: new FormControl(null, [Validators.required]),
      show_to_web: new FormControl(false),
      status: status,
      different_price: differentPrice,
      nights: nights,
      availableNights: new FormControl(null),
      hidden: new FormControl(null),

      fake_net_price: fakeNetPrice,
      fake_price: fakePrice,
      fake_margin: fakeMargin,
      fake_web_price: fakeWebPrice,

      table_foot_net_price: new FormControl(null),
      table_foot_price: new FormControl(null),
      table_foot_margin: new FormControl(null),
      table_foot_web_price: new FormControl(null),

      allotment_id: new FormControl(null),
      created_at: new FormControl(null),

      ref_number: new FormControl(null),
    });
    return formGroup;
  }

  /**
   * Обръща всяка валута в евро
   */
  private toEUR(num: number, currency: Currency = null): number | null {
    if (!currency) {
      if (
        !this.currencySelectComponent ||
        !this.currencySelectComponent.selectedCurrency ||
        !this.currencySelectComponent.selectedCurrency.exchange_rate
      ) {
        log.warn('toEUR: SELECT CURRENCY FIRST');
        return null;
      }

      return Number((num / this.currencySelectComponent.selectedCurrency.exchange_rate).toFixed(2));
    } else {
      if (!currency.exchange_rate) {
        log.warn('toEUR: INVALID CURRENCY PASSED');
        return null;
      }
      return Number((num / currency.exchange_rate).toFixed(2));
    }
  }

  private getEmptyNight(date: string = null): FormGroup {
    // log.debug('daaaaaate', date);
    const nightGroup = new FormGroup({
      id: new FormControl(null),
      date: new FormControl(date, [Validators.required]),
      status: new FormControl('', []),
      net_price: new FormControl(null, [Validators.required]),
      price: new FormControl(null, [Validators.required]),
      margin: new FormControl(null, [Validators.required, Validators.min(0)]),
      web_price: new FormControl(null, [Validators.required]),
      isAvailable: new FormControl(true),
      hotel_booking_cancellation_id: new FormControl(null),
      sale_final_item_id: new FormControl(null),
      sale_id: new FormControl(null),
      extra: new FormControl(false),
      min_night: new FormControl(false),
      guest_names: new FormControl(null),
    });

    nightGroup.get('net_price').valueChanges.subscribe((val: number) => {
      let netPriceEuroValue = this.toEUR(val);
      if (!netPriceEuroValue) {
        netPriceEuroValue = 0;
      }
      nightGroup.get('price').setValidators([Validators.required, Validators.min(netPriceEuroValue)]);
      nightGroup.get('web_price').setValidators([Validators.required, Validators.min(netPriceEuroValue)]);
      nightGroup.get('price').updateValueAndValidity();
      nightGroup.get('web_price').updateValueAndValidity();
    });

    return nightGroup;
  }

  /**
   * Добавя/премахва nights
   * На функцията и е нужен само инсдекс, за да знае къде да добавя/премахва стаи
   * @param roomIndex Индекса на стаята
   */
  private drawNights(roomIndex: number): void {
    /**
     * Пази текущия formArray с нощувките.
     * Създава се нов FormArray с брой нощувки newNightsRows
     *
     * Взима от стария всички нощувки и ги слага в новия.
     * Останалите остават празни(само с дата).
     *
     * Math-ва старата и новата нощувка по дата
     *
     */

    const newNightsRows = this.form.get('rooms')['controls'][roomIndex].get('nights_count').value;
    const currentNightsFormArr = this.form.get('rooms')['controls'][roomIndex].get('nights');
    const newNightsFormArr = new FormArray([]);
    log.debug('currentNightsFormArr', currentNightsFormArr);
    for (let i = 0; i < newNightsRows; i++) {
      const checkInDate = moment(this.form.get('rooms')['controls'][roomIndex].get('check_in').value).add(i, 'days');

      let pushed = false;
      currentNightsFormArr['controls'].forEach((night: any) => {
        if (night.value.date === checkInDate.format(this.env.apiPipeDateFormat)) {
          newNightsFormArr.push(night);
          pushed = true;
        }
      });
      if (!pushed) {
        newNightsFormArr.push(this.getEmptyNight(checkInDate.format(this.env.apiPipeDateFormat)));
      }
    }

    // const newNightsRows = this.form.get('rooms')['controls'][roomIndex].get('nights_count').value;
    // const currentNightsFormArr = this.form.get('rooms')['controls'][roomIndex].get('nights');
    // const newNightsFormArr = new FormArray([]);
    // log.debug('currentNightsFormArr', currentNightsFormArr);
    // for (let i = 0; i < newNightsRows; i++) {
    //   const checkInDate = moment(this.form.get('rooms')['controls'][roomIndex].get('check_in').value).add(i, 'days');
    //   newNightsFormArr.push(this.getEmptyNight(checkInDate.format('YYYY-MM-DD')));
    // }
    //
    // currentNightsFormArr['controls'].forEach((night: any) => {
    //   newNightsFormArr['controls'].forEach((newNight: any) => {
    //     if (night.value.date === newNight.value.date) {
    //       log.debug('match', night.value);
    //         newNight.patchValue(night.value);
    //         newNight.updateValueAndValidity(night.value);
    //       log.debug('newNight', newNight);
    //     }
    //   });
    // });

    this.form.get('rooms')['controls'][roomIndex].setControl('nights', newNightsFormArr);

    this.form.get('rooms')['controls'][roomIndex].get('nights').updateValueAndValidity();

    // const newNightsRows = this.form.get('rooms')['controls'][roomIndex].get('nights_count').value;
    // const currentNightsRows = this.form.get('rooms')['controls'][roomIndex].get('nights').value.length;
    // const nightsRowsDifference = newNightsRows - currentNightsRows;

    // if (currentNightsRows < newNightsRows) {
    //   // добавя нужните редове
    //   for (let i = 0; i < nightsRowsDifference; i++) {
    //     const roomsLength = this.form.get('rooms')['controls'][roomIndex].get('nights').value.length;
    //     const date = moment(this.form.get('rooms')['controls'][roomIndex].get('check_in').value)
    //       .add(roomsLength, 'days')
    //       .format('YYYY-MM-DD');
    //     log.debug('date', date);
    //     this.form
    //       .get('rooms')
    //       ['controls'][roomIndex].get('nights')
    //       .push(this.getEmptyNight(date));
    //   }
    // } else if (currentNightsRows >= newNightsRows) {
    //   // трие излишните редове
    //   this.form
    //     .get('rooms')
    //     ['controls'][roomIndex].get('nights')
    //     ['controls'].splice(newNightsRows, currentNightsRows);
    // }
    // this.form
    //   .get('rooms')
    //   ['controls'][roomIndex].get('nights')
    //   .updateValueAndValidity();
  }

  /**
   * Тази функция взима стойност от fake FormControl и го попълва истинския(реалния не fake) FormControl
   * @param roomIndex Индекса на стаята
   * @param FormControlName Името на FormControl-a
   * @todo: тук може да се направи с масив от стрингове ако са много, да не се вика 100 пъти
   */
  private fakeControlsChangeHandle(roomIndex: number, FormControlName: string): void {
    const fakeControlValue = this.form.get('rooms')['controls'][roomIndex].get('fake_' + FormControlName).value;
    this.form
      .get('rooms')
      ['controls'][roomIndex].get('nights')
      ['controls'].forEach((control: any) => {
        /**
         * Ако стаята не е available skip (или net price, венко каза така)
         */
        //  if (control.get('isAvailable').value) {
        control.get(FormControlName).setValue(fakeControlValue);
        //  }
      });
    this.form.get('rooms')['controls'][roomIndex].get('nights').updateValueAndValidity();
  }
}
